import { FC, useEffect, useState } from 'react'

import { nest } from 'api'
import { Button } from 'components/button'
import { Dialog, DialogProps } from 'components/dialog/Dialog'
import { useDialogContext } from 'components/dialog/utils'
import { Form, useForm } from 'components/form'
import { MaskedTextFormField } from 'components/form-field/TextFormField/MaskedTextFormField'
import { Heading } from 'components/heading'
import { ContactOurAgent } from 'components/support-link/support-link'

import classes from './verify-email-dialog.module.css'
import {
  validateErrorEnum,
  VerifyEmailDialogContentProps,
  FormData,
  verificationForEnum,
} from './types'

const VerifyEmailDialogContent = ({
  email,
  uuid,
  verificationFor,
  onSuccess,
}: VerifyEmailDialogContentProps) => {
  const { close } = useDialogContext()
  const formMethods = useForm<FormData>()
  const [error, setError] = useState<validateErrorEnum | null>(null)
  const { setFocus, setValue } = formMethods

  useEffect(() => {
    setFocus('verifyCode')
  }, [setFocus])

  const handleSubmit = async (formData: FormData, verifFor: verificationForEnum) => {
    try {
      const token = formData.verifyCode.toString()

      if (verifFor === verificationForEnum.broker) {
        await nest.broker.validateCodeFromEmail(uuid, token)
      }

      if (verifFor === verificationForEnum.investor) {
        await nest.investor.validate({
          uuid,
          token,
        })
      }

      if (verifFor === verificationForEnum.purchaser) {
        await nest.purchaser.validate({ uuid, token })
      }

      close()
      onSuccess()
    } catch (error) {
      const errorName = error.response.data.error

      if (errorName === validateErrorEnum.ExpiredToken) {
        setError(errorName)
        setValue('verifyCode', '')
      }

      if (errorName === validateErrorEnum.TooManyRequests) {
        setError(errorName)
      }

      if (errorName === validateErrorEnum.Unauthorized) {
        setError(errorName)
      }
    }
  }

  if (error === validateErrorEnum.TooManyRequests) {
    return (
      <>
        <Heading theme="h2">Try again later</Heading>
        <p className={classes.error}>
          You have made too many failed attempts.
          <br />
          Please wait 5 minutes and try again.
        </p>
        <div className={classes.actions}>
          <Button theme="primary" onClick={() => close()}>
            OK
          </Button>
        </div>
      </>
    )
  }

  return (
    <Form
      onSubmit={(formData) => handleSubmit(formData, verificationFor)}
      formMethods={formMethods}
      className={classes.form}
    >
      {!error && (
        <>
          <Heading theme="h2">Check your email</Heading>
          <p>
            We have emailed a unique verification code to <em className={classes.email}>{email}</em>
            .
          </p>
          <p id="verify-email-dialog-code-input-label">Please enter your code below to continue.</p>
        </>
      )}
      {error === validateErrorEnum.ExpiredToken && (
        <>
          <Heading theme="h2">Check your email</Heading>
          <p className={classes.error}>
            Sorry, the verification code you entered has expired.
            <br />
            We have emailed a new code to <em className={classes.email}>{email}</em>.
          </p>
          <p id="verify-email-dialog-code-input-label">Please enter your code below to continue:</p>
        </>
      )}
      {error === validateErrorEnum.Unauthorized && (
        <>
          <Heading theme="h2">Check your email</Heading>
          <p>
            We have emailed a unique verification code to <em className={classes.email}>{email}</em>
            .
          </p>
          <p className={classes.error} id="verify-email-dialog-code-input-label">
            Sorry, the code you entered is incorrect. Please check for typos and try again.
          </p>
        </>
      )}
      <MaskedTextFormField
        aria-labelledby="verify-email-dialog-code-input-label"
        className={classes.field}
        mask="0000"
        placeholder="####"
        name="verifyCode"
      />
      <div className={classes.actions}>
        <Button theme="secondary" onClick={() => close()}>
          Change Email
        </Button>
        <Button type="submit">Continue</Button>
      </div>
      <p>
        If you don’t receive an email within 5 minutes, <ContactOurAgent />.
      </p>
    </Form>
  )
}

type VerifyEmailDialogProps = DialogProps & VerifyEmailDialogContentProps

export const VerifyEmailDialog: FC<VerifyEmailDialogProps> = ({
  email,
  uuid,
  verificationFor,
  onSuccess,
  ...props
}) => {
  return (
    <Dialog {...props} className={classes.dialog} withoutHeader>
      <VerifyEmailDialogContent
        email={email}
        uuid={uuid}
        verificationFor={verificationFor}
        onSuccess={onSuccess}
      />
    </Dialog>
  )
}
